import { ArtButton, ArtDialog, ArtTable, ArtTableHandle, confirmDialog, HeadCell, SSRTableBuilder, useApiService } from "@art/shared-ui";
import { faEdit, faExclamationTriangle, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useRef, useState } from "react";
import ArtProjectsAddPage from "./add-dialog.component";
import { Box, Chip, Tooltip } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { Link, useSearchParams } from "react-router-dom";
import { usePageWrapper } from "../../../hooks";
import { AttributePermissionGate, useAttributePermission } from "../../permissions";
import "../application-users/user.css"
import { tableParamNav } from "../../../utils/table-utils";


function ArtProjectPage() {
    const [queryParams, setQueryParams]: any = useSearchParams();

    const tableRef = useRef<ArtTableHandle>(null);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [selectedData, setSelectedData]: [Data | null, any] = useState(null);
    const apiService = useApiService();
    const appState = useSelector((state: RootState) => state.appstate);
    const projectID = appState.project_key;
    const canEditProjects = useAttributePermission("PLATFORM_SETTINGS", "PROJECT_MANAGEMENT", "EditProjects");
    const canDeleteProjects = useAttributePermission("PLATFORM_SETTINGS", "PROJECT_MANAGEMENT", "DeleteProjects");

    interface Data {
        id: string;
        title: string;
        tenant_name: string;
        project_key: string;
        is_deleted: boolean;
        retain_on_downgrade: boolean;
    }

    const isRowDisabled = (row: Data) =>
        row.is_deleted === true && row.retain_on_downgrade === false;

    const disabledSx = (row: Data) =>
        isRowDisabled(row)
            ? { opacity: 0.5, cursor: "not-allowed" }
            : {};

    const headCells: HeadCell<Data>[] = [
        {
            id: 'title',
            numeric: false,
            disablePadding: false,
            label: 'Title',
            getColData: (row) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, ...disabledSx(row) }}>
                    <Link to={`/platform-settings/projects/${encodeURIComponent(row.project_key)}`} style={{ color: "inherit", fontWeight: 650, textDecoration: "none" }}>
                        {row.title}
                    </Link>
                    {isRowDisabled(row) && (
                        <Tooltip
                            title="This project was archived by an earlier subscription change. Recover it in Platform Settings → Usage → Platform limits."
                            arrow
                        >
                            <Chip
                                icon={
                                    <FontAwesomeIcon
                                        icon={faExclamationTriangle}
                                        style={{ fontSize: 12, marginLeft: 6 }}
                                    />
                                }
                                label="Archived project"
                                variant="outlined"
                                size="small"
                                color="warning"
                                sx={{
                                    fontWeight: 600,
                                    pointerEvents: "auto",
                                    cursor: "help",
                                }}
                            />
                        </Tooltip>
                    )}
                </Box>
            ),
        },
        {
            id: 'tenant_name',
            numeric: false,
            disablePadding: false,
            label: 'Workspace',
            getColData: (row) => (
                <Box sx={disabledSx(row)}>{row.tenant_name}</Box>
            ),
        },
        {
            id: 'project_key',
            numeric: false,
            disablePadding: false,
            label: 'Project Key',
            getColData: (row) => (
                <Box sx={disabledSx(row)}>{row.project_key}</Box>
            ),
        }
    ]

    const tableBuilder = SSRTableBuilder<Data>({
        endpoint: "/projects",
        method: "get",
        apiService: apiService,
        onFetch: (params) => {

            let currentPage = params["page"] || "1"
            let searchText = params["search_text"] || "" // search 

            //page and search added to params
            const newParams: any = {
                page: currentPage
            };
            if (searchText) {
                newParams.search = searchText;
            }
            setQueryParams(newParams, tableParamNav(queryParams, "page", currentPage));  // Update this

            return {
                "page": currentPage,
                "search_text": searchText
            }
        },
        onResponse: (data) => {
            return {
                data: data.data.projects ?? [],
                total: data.data.total_count
            };
        }
    })


    const openAddProjectDialog = (data?: Data) => {
        setSelectedData(data);
        setAddDialogOpen(true);
    }

    const closeAddProjectDialog = () => {
        setSelectedData(null);
        setAddDialogOpen(false);
    }

    const onSave = () => {
        closeAddProjectDialog();
        tableRef.current?.refresh(0);
    }

    return <>
        <ArtDialog title={selectedData ? "Edit Project" : "Add Project"}
            open={addDialogOpen}
            onClose={closeAddProjectDialog}
            type="form"
            actionBuilder={(props) => {
                return <Box sx={{ display: 'flex', gap: '15px' }}>
                    <ArtButton buttonType="button" dataQa="btn-save" label={selectedData ? "Update" : "Add"} onClick={() => props.submit?.()} />
                    <ArtButton buttonType="button" dataQa="btn-reset" label="Reset" variant="outlined" color="error" onClick={() => props.reset?.()} />
                </Box>
            }}>

            <ArtProjectsAddPage data={selectedData} onSave={onSave} />
        </ArtDialog>
        <Box className="non-clickable-table">
            <ArtTable ref={tableRef} headCells={headCells}
                builder={tableBuilder}
                // search={{ enabled: true, value: "" }}
                search={{
                    enabled: true,
                    value: queryParams.get("search") || "" // check and add the search text from params
                }}
                pagination={{
                    initialPage: (parseInt(queryParams.get("page") || "1")) - 1
                }}


                toolbarActions={[
                    <AttributePermissionGate application="PLATFORM_SETTINGS" feature="PROJECT_MANAGEMENT" attribute="CreateProjects">
                        <ArtButton variant="contained" dataQa="btn-add" label="Add" icon={faPlus} onClick={() => {
                            openAddProjectDialog();
                        }} />
                    </AttributePermissionGate>
                ]}
                rowActions={
                    ([
                        canEditProjects && {
                            icon: faEdit,
                            label: "Edit",
                            onClick: (row: Data) => {
                                if (isRowDisabled(row)) return;
                                openAddProjectDialog(row);
                            }
                        },
                        canDeleteProjects && {
                            icon: faTrash,
                            label: "Delete",
                            onClick: (row: Data) => {
                                if (isRowDisabled(row)) return;
                                confirmDialog("confirm", "Do you want to delete?").then(() => {
                                    if (row.project_key === projectID) {
                                        apiService.showSnack("You cannot delete the current project", "error");
                                        return;
                                    } else {
                                        apiService.deleteData(`/projects/${row.id}`, {
                                            onSuccess: () => {
                                                onSave();
                                            },
                                            onError: (err: any) => {
                                                console.log(err);
                                            }
                                        })();
                                    }
                                })
                            }
                        }
                    ].filter(Boolean) as any)}
            />
        </Box>

    </>
}

export default usePageWrapper(ArtProjectPage, {
    page_title: "Project"
});
